/**
 * @fileoverview Hook de UI Layer para gestión del estado de la interfaz de filtros del inbox
 * @module useInboxFiltersUI
 * @category Hook - UI Layer
 * @since 1.0.0
 * @version 3.1.0
 */

import { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useInboxFilterOptions from '../data/useInboxFilterOptions';

/**
 * @typedef {Object} FilterConfig
 * @description Configuración individual para cada filtro del inbox
 * @property {'select'|'multi-select'|'multi-select-group'} type - Tipo de componente de filtro
 * @property {string} key - Clave única del filtro para identificación
 * @property {string} label - Etiqueta traducida mostrada al usuario
 * @property {Array} options - Opciones disponibles para selección
 * @property {string} placeholder - Texto de placeholder traducido
 * @property {string} [addButtonLabel] - Etiqueta para botón adicional (multi-select-group)
 * @property {string} [dependencyKey] - Clave del filtro padre para filtros dependientes
 * @property {string} [parentOptionsKey] - Propiedad del objeto padre que contiene opciones
 */

/**
 * @typedef {Object} FilterChangeValues
 * @description Estructura de datos para cambios de filtros
 * @property {string} filterKey - Clave del filtro que cambió
 * @property {any} filterValue - Nuevo valor del filtro
 */

/**
 * @typedef {Object} InboxFiltersUIReturn
 * @description Objeto retornado por el hook con estado y handlers de UI
 * @property {Object} activeFilters - Filtros actualmente aplicados en la UI
 * @property {FilterConfig[]} filtersConfig - Configuración completa de todos los filtros
 * @property {function} handleFilterChange - Handler para cambios individuales de filtros
 * @property {function} handleApplyFilters - Handler para aplicar todos los filtros activos
 * @property {function} handleClearFilters - Handler para limpiar todos los filtros
 * @property {boolean} isLoading - Estado de carga global de opciones de filtro
 */

/**
 * Hook de UI Layer para gestión del estado de la interfaz de filtros del inbox.
 * 
 * Implementa el patrón de **UI Layer** con separación estricta de responsabilidades,
 * enfocándose en el estado de la interfaz y la configuración de componentes de filtro.
 * 
 * @function useInboxFiltersUI
 * @category Hook - UI Layer
 * 
 * @description
 * **Arquitectura Optimizada (v3.1.0):**
 * - ✅ **UI Layer**: Solo estado de interfaz, configuración de filtros y handlers de UI
 * - ✅ **Data Integration**: Consume datos del Data Layer sin duplicar lógica de carga
 * - ✅ **Performance**: useMemo, useCallback y optimizaciones de re-renderizado
 * - ✅ **i18n Integration**: Integración completa con sistema de traducciones
 * - ✅ **Debug Tracking**: Monitoreo de re-renders en desarrollo
 * 
 * **Responsabilidades:**
 * - Gestión del estado local de filtros activos en la UI
 * - Configuración de tipos y propiedades de componentes de filtro
 * - Handlers para aplicar, limpiar y cambiar filtros
 * - Integración con sistema de traducciones para labels y placeholders
 * - Gestión de dependencias entre filtros (filtros anidados)
 * - Optimización de re-renders mediante memoización
 * 
 * **NO Maneja:**
 * - ❌ Carga de datos de opciones de filtro (delegado al Data Layer)
 * - ❌ Lógica de negocio o validaciones de filtros
 * - ❌ Persistencia o sincronización con backend
 * - ❌ Transformación de datos de filtros
 * 
 * **Flujo de UI:**
 * 1. **Inicialización**: Establece filtros por defecto del estado inicial
 * 2. **Configuración**: Define tipos de componentes y propiedades de cada filtro
 * 3. **Interacción**: Gestiona cambios de usuario en filtros individuales
 * 4. **Aplicación**: Envía filtros activos al componente padre via callback
 * 5. **Limpieza**: Resetea estado local y notifica cambios al componente padre
 * 
 * **Tipos de Filtros Soportados:**
 * - **select**: Selección única de una opción
 * - **multi-select**: Selección múltiple de opciones
 * - **multi-select-group**: Selección múltiple con grupos (incluir/excluir)
 * - **Filtros Dependientes**: Filtros que dependen de la selección de otros
 * 
 * **Estructura de Filtros Activos:**
 * ```javascript
 * {
 *   agent_id: ["agent_123", "agent_456"],
 *   tags_tag_id_in: ["tag_789"],
 *   tags_tag_id_not_in: ["tag_101"],
 *   campaign_id: ["campaign_112"],
 *   funnel_id: "funnel_131",
 *   funnel_steps_id_in: ["step_415"]
 * }
 * ```
 * 
 * @param {Object} [defaultFilters={}] - Filtros por defecto para inicializar el estado
 * @param {function} [onFilterChange] - Callback para notificar cambios de filtros al componente padre
 * @param {string} onFilterChange.filterKey - Primera clave ('advancedFilters')
 * @param {Object} onFilterChange.filterValue - Objeto con todos los filtros activos
 * 
 * @returns {InboxFiltersUIReturn} Estado de UI y handlers para gestión de filtros
 * 
 * @see {@link useInboxFilterOptions} Para carga de opciones de filtro (Data Layer)
 * @see {@link InboxFiltersComponent} Para componente que consume este hook
 * @see {@link useInboxFiltersForm} Para validación de formularios de filtro
 * 
 * @since 1.0.0
 * @version 3.1.0 - Optimizaciones de performance y debug tracking
 * 
 * @example
 * ```javascript
 * // Uso básico en componente de filtros
 * const InboxFilters = () => {
 *   const handleFiltersChange = useCallback((key, filters) => {
 *     console.log('Filtros aplicados:', filters);
 *     // Aplicar filtros al listado del inbox
 *   }, []);
 * 
 *   const {
 *     activeFilters,
 *     filtersConfig,
 *     handleFilterChange,
 *     handleApplyFilters,
 *     handleClearFilters,
 *     isLoading
 *   } = useInboxFiltersUI({}, handleFiltersChange);
 * 
 *   if (isLoading) {
 *     return <LoadingSpinner />;
 *   }
 * 
 *   return (
 *     <div className="tw-wrapper">
 *       {filtersConfig.map(filter => (
 *         <FilterComponent
 *           key={filter.key}
 *           type={filter.type}
 *           label={filter.label}
 *           options={filter.options}
 *           value={activeFilters[filter.key]}
 *           onChange={(value) => handleFilterChange({
 *             filterKey: filter.key,
 *             filterValue: value
 *           })}
 *           placeholder={filter.placeholder}
 *         />
 *       ))}
 *       <ButtonMC onClick={handleApplyFilters}>Aplicar Filtros</ButtonMC>
 *       <ButtonMC onClick={handleClearFilters}>Limpiar Filtros</ButtonMC>
 *     </div>
 *   );
 * };
 * ```
 * 
 * @example
 * ```javascript
 * // Uso avanzado con filtros dependientes
 * const AdvancedInboxFilters = () => {
 *   const [selectedFunnel, setSelectedFunnel] = useState(null);
 *   
 *   const {
 *     activeFilters,
 *     filtersConfig,
 *     handleFilterChange,
 *     handleApplyFilters
 *   } = useInboxFiltersUI({ funnel_id: 'funnel_123' });
 * 
 *   const funnelStepsFilter = filtersConfig.find(f => f.key === 'funnel_steps_id_in');
 *   const availableSteps = selectedFunnel?.funnel_steps || [];
 * 
 *   return (
 *     <div className="tw-wrapper">
 *       <SelectMC
 *         options={filtersConfig.find(f => f.key === 'funnel_id').options}
 *         onChange={(funnel) => {
 *           setSelectedFunnel(funnel);
 *           handleFilterChange({
 *             filterKey: 'funnel_id',
 *             filterValue: funnel?.id
 *           });
 *         }}
 *         placeholder="Seleccionar embudo"
 *       />
 *       
 *       {availableSteps.length > 0 && (
 *         <MultiSelectMC
 *           options={availableSteps}
 *           onChange={(steps) => handleFilterChange({
 *             filterKey: 'funnel_steps_id_in',
 *             filterValue: steps.map(s => s.id)
 *           })}
 *           placeholder="Seleccionar pasos del embudo"
 *         />
 *       )}
 *       
 *       <ButtonMC onClick={handleApplyFilters}>
 *         Aplicar Filtros Avanzados
 *       </ButtonMC>
 *     </div>
 *   );
 * };
 * ```
 */
const useInboxFiltersUI = (defaultFilters = {}, onFilterChange) => {
  /**
   * Sistema de traducciones específico para el namespace InboxMC.
   * 
   * @constant {function} t
   * @memberof useInboxFiltersUI
   * @private
   */
  const { t } = useTranslation('InboxMC');

  const aiCopilotsEnabled = useSelector(
    (state) => state.retailerUsersReducer.retailer_info?.ai_copilots_enabled
  );

  const leadTemperatureOptions = useMemo(() => [
    { id: 'very_high', label: t('filters.lead_temperature.very_hot') },
    { id: 'high', label: t('filters.lead_temperature.hot') },
    { id: 'medium', label: t('filters.lead_temperature.warm') },
    { id: 'low', label: t('filters.lead_temperature.cool') },
    { id: 'very_low', label: t('filters.lead_temperature.cold') }
  ], [t]);

  /**
   * Estado local de filtros activos en la interfaz de usuario.
   * 
   * Mantiene el estado actual de todos los filtros seleccionados por el usuario
   * antes de aplicarlos definitivamente.
   * 
   * @state {Object} activeFilters
   * @memberof useInboxFiltersUI
   * @private
   * 
   * @description
   * **Características:**
   * - Inicializado con defaultFilters para preservar estado previo
   * - Solo UI state - no persiste automáticamente
   * - Estructura clave-valor para diferentes tipos de filtros
   * - Reseteable mediante handleClearFilters
   * 
   * **Estructura Típica:**
   * ```javascript
   * {
   *   agent_id: ["agent_123", "agent_456"],
   *   tags_tag_id_in: ["tag_789"],
   *   campaign_id: ["campaign_112"],
   *   funnel_id: "funnel_131"
   * }
   * ```
   */
  const [activeFilters, setActiveFilters] = useState(() => ({ ...defaultFilters }));

  /**
   * Integración con Data Layer para obtener opciones de filtro pre-cargadas.
   * 
   * Consume datos normalizados del hook de Data Layer sin duplicar
   * la lógica de carga o transformación de datos.
   * 
   * @hook {Object} filterOptionsData
   * @memberof useInboxFiltersUI
   * @private
   * 
   * @description
   * **Datos Consumidos:**
   * - agentListOptions: Agentes disponibles para filtrado
   * - customerListOptions: Listas de contactos del retailer
   * - campaignOptions: Campañas activas normalizadas
   * - funnelOptions: Embudos con pasos anidados
   * - tagOptions: Etiquetas con información de color
   * - globalLoading: Estado consolidado de carga
   * 
   * **Separación de Responsabilidades:**
   * - UI Layer (este hook): Solo consume y configura para UI
   * - Data Layer (useInboxFilterOptions): Carga y normaliza datos
   * 
   * @see {@link useInboxFilterOptions} Hook de Data Layer
   */
  const {
    agentListOptions,
    customerListOptions,
    campaignOptions,
    funnelOptions,
    tagOptions,
    loading: globalLoading
  } = useInboxFilterOptions();

  /**
   * Handler para gestionar cambios individuales de filtros en la UI.
   * 
   * Actualiza el estado local de filtros activos cuando el usuario
   * modifica un filtro específico en la interfaz.
   * 
   * @async
   * @function handleFilterChange
   * @memberof useInboxFiltersUI
   * 
   * @description
   * **Comportamiento:**
   * - Actualiza solo el filtro especificado sin afectar otros
   * - Preserva el estado previo de filtros no modificados
   * - No aplica filtros automáticamente (requiere handleApplyFilters)
   * - Optimizado con useCallback para evitar re-renders
   * 
   * **Flujo de Actualización:**
   * 1. Recibe objeto con filterKey y filterValue
   * 2. Actualiza activeFilters preservando estado previo
   * 3. Componente se re-renderiza con nuevo estado
   * 4. Usuario debe aplicar filtros explícitamente
   * 
   * @param {FilterChangeValues} values - Datos del filtro que cambió
   * @param {string} values.filterKey - Clave única del filtro
   * @param {any} values.filterValue - Nuevo valor del filtro
   * 
   * @example
   * ```javascript
   * // Cambio en filtro de agentes
   * handleFilterChange({
   *   filterKey: 'agent_id',
   *   filterValue: ['agent_123', 'agent_456']
   * });
   * 
   * // Cambio en filtro de embudo
   * handleFilterChange({
   *   filterKey: 'funnel_id',
   *   filterValue: 'funnel_789'
   * });
   * ```
   */
  const handleFilterChange = useCallback((values) => {
    setActiveFilters(prev => ({
      ...prev,
      [values.filterKey]: values.filterValue
    }));
  }, []);

  /**
   * Handler para aplicar todos los filtros activos al componente padre.
   * 
   * Envía todos los filtros configurados en la UI al componente padre
   * para que aplique el filtrado en el listado del inbox.
   * 
   * @async
   * @function handleApplyFilters
   * @memberof useInboxFiltersUI
   * 
   * @description
   * **Comportamiento:**
   * - Envía todos los activeFilters al componente padre via callback
   * - Utiliza clave estándar 'advancedFilters' para identificación
   * - No modifica estado local - solo notifica cambios
   * - Optimizado para evitar llamadas innecesarias
   * 
   * **Protocolo de Comunicación:**
   * ```javascript
   * onFilterChange('advancedFilters', {
   *   agent_id: ['agent_123'],
   *   tags_tag_id_in: ['tag_456'],
   *   campaign_id: ['campaign_789']
   * });
   * ```
   * 
   * **Casos de Uso:**
   * - Usuario hace clic en "Aplicar Filtros"
   * - Aplicación automática tras selección (opcional)
   * - Sincronización con componente padre
   * 
   * @example
   * ```javascript
   * // En el componente padre
   * const handleFiltersChange = (key, filters) => {
   *   if (key === 'advancedFilters') {
   *     setInboxFilters(filters);
   *     fetchFilteredMessages(filters);
   *   }
   * };
   * 
   * // En el componente de filtros
   * <ButtonMC onClick={handleApplyFilters}>
   *   Aplicar Filtros
   * </ButtonMC>
   * ```
   */
  const handleApplyFilters = useCallback(() => {
    // Llamar onFilterChange con 'advancedFilters' como primera clave y activeFilters como segunda
    onFilterChange?.('advancedFilters', activeFilters);
  }, [activeFilters, onFilterChange]);

  /**
   * Handler para limpiar todos los filtros activos y resetear la UI.
   * 
   * Resetea el estado local de filtros y notifica al componente padre
   * para remover el filtrado aplicado.
   * 
   * @async
   * @function handleClearFilters
   * @memberof useInboxFiltersUI
   * 
   * @description
   * **Operaciones Realizadas:**
   * 1. Resetea activeFilters a objeto vacío
   * 2. Actualiza estado local de la UI
   * 3. Notifica al componente padre con filtros vacíos
   * 4. Componente padre puede remover filtrado del listado
   * 
   * **Efectos en la UI:**
   * - Limpia todas las selecciones de filtros
   * - Resetea componentes de filtro a estado inicial
   * - Muestra listado completo sin filtrado
   * - Preserva opciones disponibles
   * 
   * **Protocolo de Comunicación:**
   * ```javascript
   * onFilterChange('advancedFilters', {});
   * ```
   * 
   * @example
   * ```javascript
   * // Uso en componente
   * <ButtonMC 
   *   onClick={handleClearFilters}
   *   variant="outline"
   * >
   *   Limpiar Filtros
   * </ButtonMC>
   * 
   * // En el componente padre
   * const handleFiltersChange = (key, filters) => {
   *   if (key === 'advancedFilters' && Object.keys(filters).length === 0) {
   *     // Remover filtrado y mostrar todos los mensajes
   *     setInboxFilters({});
   *     fetchAllMessages();
   *   }
   * };
   * ```
   */
  const handleClearFilters = useCallback(() => {
    const clearedFilters = {};
    setActiveFilters(clearedFilters);
    // Llamar onFilterChange con 'advancedFilters' como primera clave y objeto vacío como segunda
    onFilterChange?.('advancedFilters', clearedFilters);
  }, [onFilterChange]);

  /**
   * Configuración completa de todos los filtros disponibles en la UI.
   * 
   * Define la estructura, tipos de componentes, opciones disponibles y
   * comportamiento de cada filtro del sistema de inbox.
   * 
   * @constant {FilterConfig[]} filtersConfig
   * @memberof useInboxFiltersUI
   * 
   * @description
   * **Tipos de Filtros Implementados:**
   * 
   * 1. **multi-select** - Selección múltiple estándar:
   *    - agent_id: Filtrado por agentes del retailer
   *    - contact_group_id: Filtrado por listas de contactos
   *    - campaign_id: Filtrado por campañas
   *    - funnel_steps_id_in: Filtrado por pasos de embudo (dependiente)
   * 
   * 2. **multi-select-group** - Selección con grupos (incluir/excluir):
   *    - tags_tag_id_in: Etiquetas a incluir
   *    - tags_tag_id_not_in: Etiquetas a excluir
   * 
   * 3. **select** - Selección única:
   *    - funnel_id: Embudo de ventas (padre de funnel_steps)
   * 
   * **Filtros Dependientes:**
   * - funnel_steps_id_in depende de funnel_id
   * - Se configura con dependencyKey y parentOptionsKey
   * - Solo se muestra cuando el filtro padre tiene valor
   * 
   * **Estructura de Configuración:**
   * ```javascript
   * {
   *   type: 'multi-select',           // Tipo de componente
   *   key: 'agent_id',                // Clave única del filtro
   *   label: 'Agente',                // Label traducido
   *   options: [...],                 // Opciones del Data Layer
   *   placeholder: 'Seleccionar...',  // Placeholder traducido
   *   dependencyKey: 'parent_filter', // Para filtros dependientes
   *   parentOptionsKey: 'sub_options' // Propiedad con opciones anidadas
   * }
   * ```
   * 
   * **Optimizaciones:**
   * - useMemo evita recalculación en cada render
   * - Dependencias mínimas para re-creación
   * - Reutilización de opciones del Data Layer
   * - Traducción dinámica con react-i18next
   * 
   * @see {@link FilterConfig} Definición de estructura de filtro
   */
  const filtersConfig = useMemo(() => [
    {
      type: 'multi-select',
      key: 'agent_id',
      label: t('filters.agent.label'),
      options: agentListOptions,
      placeholder: t('filters.agent.placeholder')
    },
    {
      type: 'multi-select-group',
      key: 'tags_tag_id_in',
      label: t('filters.tags.include'),
      options: tagOptions,
      placeholder: t('filters.tags.placeholder'),
      addButtonLabel: t('filters.tags.addButton')
    },
    {
      type: 'multi-select-group',
      key: 'tags_tag_id_not_in',
      label: t('filters.tags.exclude'),
      options: tagOptions,
      placeholder: t('filters.tags.placeholder'),
      addButtonLabel: t('filters.tags.addButton')
    },
    {
      type: 'multi-select',
      key: 'contact_group_id',
      label: t('filters.lists.label'),
      options: customerListOptions,
      placeholder: t('filters.lists.placeholder')
    },
    {
      type: 'multi-select',
      key: 'campaign_id',
      label: t('filters.campaigns.label'),
      options: campaignOptions,
      placeholder: t('filters.campaigns.placeholder')
    },
    ...(aiCopilotsEnabled ? [{
      type: 'select',
      key: 'lead_temperature',
      label: t('filters.lead_temperature.label'),
      options: leadTemperatureOptions,
      placeholder: t('filters.lead_temperature.placeholder')
    }] : [])
    // {
    //   type: 'select',
    //   key: 'funnel_id',
    //   label: t('filters.funnel.label'),
    //   options: funnelOptions,
    //   placeholder: t('filters.funnel.placeholder')
    // },
    // {
    //   type: 'multi-select',
    //   key: 'funnel_steps_id_in',
    //   label: t('filters.funnel_stage.label'),
    //   dependencyKey: 'funnel_id',
    //   parentOptionsKey: 'funnel_steps',
    //   placeholder: t('filters.funnel_stage.placeholder')
    // }
  ], [
    t,
    agentListOptions,
    customerListOptions,
    tagOptions,
    campaignOptions,
    funnelOptions,
    aiCopilotsEnabled,
    leadTemperatureOptions
  ]);

  /**
   * Objeto de retorno memoizado con toda la lógica de UI encapsulada.
   * 
   * Optimiza re-renders mediante memoización del objeto de retorno completo,
   * asegurando que los componentes consumidores solo se re-rendericen cuando
   * el estado o handlers realmente cambien.
   * 
   * @function returnObject
   * @memberof useInboxFiltersUI
   * @private
   * 
   * @description
   * **Optimizaciones de Performance:**
   * - useMemo evita creación de nuevo objeto en cada render
   * - Array de dependencias específico con todos los elementos necesarios
   * - Estructura inmutable que facilita comparaciones en React
   * - Handlers memoizados para evitar re-renderizados en componentes hijo
   * 
   * **Contenido del Objeto de Retorno:**
   * 
   * 1. **Estado:**
   *    - activeFilters: Filtros actualmente configurados en la UI
   * 
   * 2. **Configuración:**
   *    - filtersConfig: Array de configuración de todos los filtros
   * 
   * 3. **Handlers de UI:**
   *    - handleFilterChange: Para cambios individuales de filtros
   *    - handleApplyFilters: Para aplicar todos los filtros activos
   *    - handleClearFilters: Para resetear todos los filtros
   * 
   * 4. **Estado Global:**
   *    - isLoading: Estado consolidado de carga del Data Layer
   * 
   * **Patron de Consumo:**
   * ```javascript
   * const {
   *   activeFilters,      // Estado actual
   *   filtersConfig,      // Configuración de UI
   *   handleFilterChange, // Cambios individuales
   *   handleApplyFilters, // Aplicar filtros
   *   handleClearFilters, // Limpiar filtros
   *   isLoading          // Estado de carga
   * } = useInboxFiltersUI();
   * ```
   * 
   * **Garantía de Inmutabilidad:**
   * El objeto retornado es inmutable y solo cambia cuando las dependencias
   * reales del useMemo se modifican, evitando re-renders innecesarios.
   * 
   * @returns {InboxFiltersUIReturn} Objeto inmutable con estado y handlers
   */
  return useMemo(() => ({
    // Estado
    activeFilters,

    // Configuración
    filtersConfig,

    // Handlers - lógica de UI
    handleFilterChange,
    handleApplyFilters,
    handleClearFilters,

    // Estado global de carga
    isLoading: globalLoading
  }), [
    activeFilters,
    filtersConfig,
    handleFilterChange,
    handleApplyFilters,
    handleClearFilters,
    globalLoading
  ]);
};

export default useInboxFiltersUI;